import { Spinner } from './chunk-T2TNXQRV.js';
import { Textarea } from './chunk-375TQAFA.js';
import { Checkbox } from './chunk-KAFWCBYS.js';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './chunk-PEIGN6MO.js';
import { Field } from './chunk-B25HJXFB.js';
import { Input } from './chunk-VYHSTPFS.js';
import { Label } from './chunk-676CI6I4.js';
import { Button } from './chunk-UDB35RLY.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React3 from 'react';
import { DEFAULT_ACTIONS, ACTION_LABEL, MANAGE_ACTION, DEFAULT_RESOURCE_GROUPS, togglePermission, setGroupActions, countPermissions, grantedGroupCount, hasPermission, toScopeActions, enforceFromRolePermissions } from '@authe/fe-commons/auth';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';

var PermissionContext = React3.createContext(null);
function PermissionProvider({
  children,
  permissions,
  rolePermissions,
  can,
  isLoading = false
}) {
  const value = React3.useMemo(() => {
    const check = can ?? (permissions ? (resource, action) => permissions.can(resource, action) : (resource, action) => enforceFromRolePermissions(rolePermissions, resource, action));
    return { can: check, isLoading };
  }, [can, permissions, rolePermissions, isLoading]);
  return /* @__PURE__ */ jsx(PermissionContext.Provider, { value, children });
}
function usePermissions() {
  return React3.useContext(PermissionContext) ?? { can: () => false, isLoading: false };
}
function usePermission(resource, action) {
  return usePermissions().can(resource, action);
}
function Can({ resource, action, mode = "all", children, fallback = null }) {
  const { can } = usePermissions();
  const resources = Array.isArray(resource) ? resource : [resource];
  const allowed = mode === "any" ? resources.some((r) => can(r, action)) : resources.every((r) => can(r, action));
  return /* @__PURE__ */ jsx(Fragment, { children: allowed ? children : fallback });
}
var PermissionGate = Can;
function PermissionMatrixEditor({
  matrix,
  onChange,
  resourceGroups = DEFAULT_RESOURCE_GROUPS,
  actions = [...DEFAULT_ACTIONS],
  readOnly,
  className
}) {
  const emit = (next) => {
    if (!readOnly) onChange?.(next);
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    /* @__PURE__ */ jsx("div", { className: "overflow-x-auto rounded-md border", children: /* @__PURE__ */ jsxs(Table, { className: "w-full border-collapse text-sm", children: [
      /* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsxs(TableRow, { className: "border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground", children: [
        /* @__PURE__ */ jsx(TableHead, { className: "px-3 py-2 text-left font-medium", children: "Resource" }),
        actions.map((action) => /* @__PURE__ */ jsx(TableHead, { className: "px-2 py-2 text-center font-medium", children: ACTION_LABEL[action] ?? action }, action)),
        /* @__PURE__ */ jsx(TableHead, { className: "px-2 py-2 text-center font-medium", children: ACTION_LABEL[MANAGE_ACTION] })
      ] }) }),
      /* @__PURE__ */ jsx(TableBody, { children: resourceGroups.map((group) => {
        const granted = matrix[group.id] ?? [];
        const manageOn = granted.includes(MANAGE_ACTION);
        const groupActions = group.actions ?? actions;
        return /* @__PURE__ */ jsxs(TableRow, { className: "border-b last:border-0", children: [
          /* @__PURE__ */ jsxs(TableCell, { className: "px-3 py-2", children: [
            /* @__PURE__ */ jsx("div", { className: "font-medium", children: group.label }),
            group.description ? /* @__PURE__ */ jsx("div", { className: "text-xs text-muted-foreground", children: group.description }) : null
          ] }),
          actions.map((action) => {
            const offered = groupActions.includes(action);
            const checked = manageOn || granted.includes(action);
            return /* @__PURE__ */ jsx(TableCell, { className: "px-2 py-2 text-center", children: offered ? /* @__PURE__ */ jsx(
              Checkbox,
              {
                checked,
                disabled: readOnly || manageOn,
                "aria-label": `${group.label}: ${ACTION_LABEL[action] ?? action}`,
                onCheckedChange: () => emit(togglePermission(matrix, group.id, action))
              }
            ) : /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" }) }, action);
          }),
          /* @__PURE__ */ jsx(TableCell, { className: "px-2 py-2 text-center", children: /* @__PURE__ */ jsx(
            Checkbox,
            {
              checked: manageOn,
              disabled: readOnly,
              "aria-label": `${group.label}: Manage`,
              onCheckedChange: () => emit(setGroupActions(matrix, group.id, manageOn ? [] : [MANAGE_ACTION]))
            }
          ) })
        ] }, group.id);
      }) })
    ] }) }),
    /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      countPermissions(matrix),
      " grant",
      countPermissions(matrix) === 1 ? "" : "s",
      " across",
      " ",
      grantedGroupCount(matrix),
      " resource group",
      grantedGroupCount(matrix) === 1 ? "" : "s",
      "."
    ] })
  ] });
}
function groupByResource(scopes) {
  const byResource = /* @__PURE__ */ new Map();
  for (const scope of scopes) {
    (byResource.get(scope.resource) ?? byResource.set(scope.resource, []).get(scope.resource)).push(scope);
  }
  return [...byResource.entries()].map(([resource, items]) => ({ resource, scopes: items })).sort((a, b) => a.resource.localeCompare(b.resource));
}
function matches(scope, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return scope.name.toLowerCase().includes(q) || scope.resource.toLowerCase().includes(q) || scope.action.toLowerCase().includes(q) || scope.description.toLowerCase().includes(q);
}
function PermissionScopePicker({
  matrix,
  onChange,
  scopes,
  readOnly,
  className
}) {
  const [query, setQuery] = React3.useState("");
  const filtered = React3.useMemo(() => scopes.filter((scope) => matches(scope, query)), [scopes, query]);
  const groups = React3.useMemo(() => groupByResource(filtered), [filtered]);
  const selectedCount = React3.useMemo(
    () => scopes.filter((scope) => hasPermission(matrix, scope.resource, scope.action)).length,
    [scopes, matrix]
  );
  const emit = (next) => {
    if (!readOnly) onChange?.(next);
  };
  const toggleResource = (bucket, grantAll) => {
    const actions = grantAll ? bucket.scopes.map((scope) => scope.action) : [];
    emit(setGroupActions(matrix, bucket.resource, actions));
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        type: "search",
        value: query,
        onChange: (event) => setQuery(event.target.value),
        placeholder: "Search permissions (e.g. transactions, export, \u201Cview labels\u201D)",
        "aria-label": "Search permissions"
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "max-h-[28rem] divide-y overflow-y-auto rounded-md border", children: groups.length === 0 ? /* @__PURE__ */ jsxs("div", { className: "p-4 text-sm text-muted-foreground", children: [
      "No permissions match \u201C",
      query,
      "\u201D."
    ] }) : groups.map((bucket) => {
      const grantedInGroup = bucket.scopes.filter(
        (scope) => hasPermission(matrix, scope.resource, scope.action)
      ).length;
      const allGranted = grantedInGroup === bucket.scopes.length;
      return /* @__PURE__ */ jsxs("div", { className: "p-2", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2 px-1 py-1", children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs font-semibold uppercase tracking-wide text-muted-foreground", children: bucket.resource }),
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              disabled: readOnly,
              onClick: () => toggleResource(bucket, !allGranted),
              className: "text-xs font-medium text-brand hover:underline disabled:pointer-events-none disabled:opacity-50",
              children: [
                allGranted ? "Clear all" : "Select all",
                " (",
                grantedInGroup,
                "/",
                bucket.scopes.length,
                ")"
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx("ul", { className: "flex flex-col", children: bucket.scopes.map((scope) => {
          const checked = hasPermission(matrix, scope.resource, scope.action);
          return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
            "label",
            {
              className: cn(
                "flex cursor-pointer items-start gap-2.5 rounded-sm px-1 py-1.5 hover:bg-muted/60",
                readOnly && "cursor-default"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  Checkbox,
                  {
                    checked,
                    disabled: readOnly,
                    "aria-label": scope.name,
                    className: "mt-0.5",
                    onCheckedChange: () => emit(togglePermission(matrix, scope.resource, scope.action))
                  }
                ),
                /* @__PURE__ */ jsxs("span", { className: "min-w-0 flex-1", children: [
                  /* @__PURE__ */ jsx("span", { className: "block font-mono text-xs text-foreground", children: scope.name }),
                  scope.description ? /* @__PURE__ */ jsx("span", { className: "block text-xs text-muted-foreground", children: scope.description }) : null
                ] })
              ]
            }
          ) }, scope.name);
        }) })
      ] }, bucket.resource);
    }) }),
    /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      selectedCount,
      " of ",
      scopes.length,
      " permission",
      scopes.length === 1 ? "" : "s",
      " selected."
    ] })
  ] });
}
function RoleEditor({
  role,
  onSave,
  onCancel,
  resourceGroups,
  actions,
  scopes,
  readOnly,
  className
}) {
  const locked = Boolean(readOnly || role.system);
  const baseline = React3.useMemo(() => toScopeActions(role.permissions), [role.permissions]);
  const [name, setName] = React3.useState(role.name);
  const [description, setDescription] = React3.useState(role.description ?? "");
  const [permissions, setPermissions] = React3.useState(baseline);
  const [busy, setBusy] = React3.useState(false);
  React3.useEffect(() => {
    setName(role.name);
    setDescription(role.description ?? "");
    setPermissions(baseline);
  }, [role, baseline]);
  const dirty = name !== role.name || description !== (role.description ?? "") || JSON.stringify(permissions) !== JSON.stringify(baseline);
  const save = async () => {
    if (!onSave || busy) return;
    setBusy(true);
    try {
      await onSave({ name: name.trim(), description: description.trim() || void 0, permissions });
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4", className), children: [
    /* @__PURE__ */ jsx(Field, { label: "Role name", children: /* @__PURE__ */ jsx(Input, { value: name, disabled: locked, onChange: (event) => setName(event.target.value) }) }),
    /* @__PURE__ */ jsx(Field, { label: "Description", children: /* @__PURE__ */ jsx(
      Textarea,
      {
        rows: 2,
        value: description,
        disabled: locked,
        placeholder: "What is this role for?",
        onChange: (event) => setDescription(event.target.value)
      }
    ) }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
      /* @__PURE__ */ jsx(Label, { children: "Permissions" }),
      scopes && scopes.length > 0 ? /* @__PURE__ */ jsx(PermissionScopePicker, { matrix: permissions, onChange: setPermissions, scopes, readOnly: locked }) : /* @__PURE__ */ jsx(
        PermissionMatrixEditor,
        {
          matrix: permissions,
          onChange: setPermissions,
          resourceGroups,
          actions,
          readOnly: locked
        }
      )
    ] }),
    role.system ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "This is a built-in role and can't be edited." }) : null,
    !locked && onSave ? /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-2", children: [
      onCancel ? /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", onClick: onCancel, children: "Cancel" }) : null,
      /* @__PURE__ */ jsx(Button, { type: "button", disabled: !dirty || !name.trim() || busy, onClick: () => void save(), children: busy ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2 text-current" }),
        "Saving\u2026"
      ] }) : "Save role" })
    ] }) : null
  ] });
}

export { Can, PermissionGate, PermissionMatrixEditor, PermissionProvider, PermissionScopePicker, RoleEditor, usePermission, usePermissions };
//# sourceMappingURL=chunk-7E3YYYJH.js.map
//# sourceMappingURL=chunk-7E3YYYJH.js.map